import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import '../../Marketplace/Marketplace.css';

const STATUSES = ['NEW', 'REVIEWING', 'INTERVIEW', 'ACCEPTED', 'REJECTED'];
const STATUS_COLORS = {
  NEW:       { bg: '#dbeafe', text: '#1e40af' },
  REVIEWING: { bg: '#fef3c7', text: '#92400e' },
  INTERVIEW: { bg: '#ede9fe', text: '#5b21b6' },
  ACCEPTED:  { bg: '#d1fae5', text: '#065f46' },
  REJECTED:  { bg: '#fee2e2', text: '#991b1b' },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f1f5f9', text: '#475569' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.text }}>
      {status}
    </span>
  );
}

export default function JobApplicants() {
  const { t } = useTranslation();
  const [apps, setApps]           = useState([]);
  const [counts, setCounts]       = useState({});
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState('');
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected]   = useState(null);
  const [edit, setEdit]           = useState({ status: '', adminNotes: '', interviewAt: '' });
  const [saving, setSaving]       = useState(false);

  const fetchApps = useCallback(() => {
    setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}&size=200` : '?size=200';
    axiosInstance.get(`/admin/careers/applications${q}`)
      .then(res => setApps(res.data.content || []))
      .catch(() => setMsg(t('JobApplicants.errorLoad')))
      .finally(() => setLoading(false));
  }, [statusFilter, t]);

  const fetchSummary = useCallback(() => {
    axiosInstance.get('/admin/careers/applications/summary')
      .then(res => setCounts(res.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const openDetail = (a) => {
    setSelected(a);
    setEdit({
      status: a.status || 'NEW',
      adminNotes: a.adminNotes || '',
      interviewAt: a.interviewAt ? a.interviewAt.slice(0, 16) : '',
    });
    setMsg('');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true); setMsg('');
    try {
      const body = {
        status: edit.status,
        adminNotes: edit.adminNotes,
        interviewAt: edit.interviewAt ? edit.interviewAt : null,
      };
      const res = await axiosInstance.put(`/admin/careers/applications/${selected.id}/status`, body);
      setSelected(res.data);
      fetchApps();
      fetchSummary();
      setMsg(t('JobApplicants.successUpdate'));
    } catch { setMsg(t('JobApplicants.errorUpdate')); }
    finally { setSaving(false); }
  };

  const downloadResume = async (a) => {
    try {
      const res = await axiosInstance.get(`/admin/careers/applications/${a.id}/resume`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.resumeFileName || `resume-${a.id}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { setMsg(t('JobApplicants.errorDownloadResume')); }
  };

  const filtered = apps.filter(a => {
    const s = search.toLowerCase();
    return !s
      || `${a.firstName} ${a.lastName}`.toLowerCase().includes(s)
      || a.email?.toLowerCase().includes(s)
      || a.roleTitle?.toLowerCase().includes(s);
  });

  const total = STATUSES.reduce((sum, s) => sum + (counts[s] || 0), 0);

  return (
    <div className="mp-wrap">
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-file-upload" /> {t('JobApplicants.title')}</h2>
          <p className="mp-subtitle">
            {t('JobApplicants.subtitle', { count: total, newCount: counts.NEW || 0, interviewing: counts.INTERVIEW || 0 })}
          </p>
        </div>
      </div>

      {msg && <div className="mp-msg">{msg}</div>}

      {/* KPI row */}
      <div className="mp-kpi-row">
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('JobApplicants.kpiTotal')}</div>
          <div className="mp-kpi-value">{total}</div>
        </div>
        {STATUSES.map(s => (
          <div className="mp-kpi" key={s}>
            <div className="mp-kpi-label">{t(`JobApplicants.status_${s}`)}</div>
            <div className="mp-kpi-value" style={{ color: STATUS_COLORS[s].text }}>{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="mp-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%',
              transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }} />
            <input className="mp-input" style={{ paddingLeft: 32, width: '100%' }}
              placeholder={t('JobApplicants.searchPlaceholder')} value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="mp-input" style={{ width: 180 }} value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}>
            <option value="">{t('JobApplicants.allStatuses')}</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading ? <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('JobApplicants.loading')}</div> : (
          filtered.length === 0 ? (
            <div className="mp-empty">{t('JobApplicants.empty')}</div>
          ) : (
            <table className="mp-table">
              <thead>
                <tr><th>{t('JobApplicants.colApplicant')}</th><th>{t('JobApplicants.colRole')}</th><th>{t('JobApplicants.colLocation')}</th><th>{t('JobApplicants.colLinks')}</th><th>{t('JobApplicants.colStatus')}</th><th>{t('JobApplicants.colApplied')}</th><th>{t('JobApplicants.colActions')}</th></tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.firstName} {a.lastName}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.email}</div>
                    </td>
                    <td>{a.roleTitle}</td>
                    <td>{a.location || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 10 }}>
                        {a.linkedinUrl && <a href={a.linkedinUrl} target="_blank" rel="noreferrer" title="LinkedIn"><i className="fab fa-linkedin" /></a>}
                        {a.githubUrl && <a href={a.githubUrl} target="_blank" rel="noreferrer" title="GitHub"><i className="fab fa-github" /></a>}
                        {a.portfolioUrl && <a href={a.portfolioUrl} target="_blank" rel="noreferrer" title={t('JobApplicants.portfolio')}><i className="fas fa-link" /></a>}
                        {a.resumeFileName && <button className="mp-link-btn" title={t('JobApplicants.downloadResume')}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#2563eb' }}
                          onClick={() => downloadResume(a)}><i className="fas fa-file-download" /></button>}
                      </div>
                    </td>
                    <td><StatusBadge status={a.status} /></td>
                    <td>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      <button className="mp-btn-sm" onClick={() => openDetail(a)}>{t('JobApplicants.view')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Detail / review modal */}
      {selected && (
        <div className="mp-overlay" onClick={() => setSelected(null)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header">
              <h3>{selected.firstName} {selected.lastName} — {selected.roleTitle}</h3>
              <button onClick={() => setSelected(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="mp-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div><strong>{t('JobApplicants.fieldEmail')}</strong> <a href={`mailto:${selected.email}`}>{selected.email}</a></div>
                <div><strong>{t('JobApplicants.fieldPhone')}</strong> {selected.phone || '—'}</div>
                <div><strong>{t('JobApplicants.fieldLocation')}</strong> {selected.location || '—'}</div>
                <div><strong>{t('JobApplicants.fieldWorkAuthorized')}</strong> {selected.workAuthorized == null ? '—' : (selected.workAuthorized ? t('JobApplicants.yes') : t('JobApplicants.no'))}</div>
                <div><strong>LinkedIn:</strong> {selected.linkedinUrl ? <a href={selected.linkedinUrl} target="_blank" rel="noreferrer">{t('JobApplicants.profile')}</a> : '—'}</div>
                <div><strong>GitHub:</strong> {selected.githubUrl ? <a href={selected.githubUrl} target="_blank" rel="noreferrer">{t('JobApplicants.profile')}</a> : '—'}</div>
                <div><strong>{t('JobApplicants.fieldPortfolio')}</strong> {selected.portfolioUrl ? <a href={selected.portfolioUrl} target="_blank" rel="noreferrer">{t('JobApplicants.link')}</a> : '—'}</div>
                <div><strong>{t('JobApplicants.fieldApplied')}</strong> {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</div>
              </div>

              {selected.resumeFileName && (
                <div className="mp-field">
                  <label>{t('JobApplicants.labelResume')}</label>
                  <button className="mp-btn-secondary" onClick={() => downloadResume(selected)}>
                    <i className="fas fa-file-download" /> {selected.resumeFileName}
                  </button>
                </div>
              )}

              {selected.coverNote && (
                <div className="mp-field">
                  <label>{t('JobApplicants.labelCoverNote')}</label>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#334155',
                    background: '#f8fafc', padding: 10, borderRadius: 8,
                    maxHeight: 240, overflowY: 'auto' }}>{selected.coverNote}</div>
                </div>
              )}

              <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />

              <div className="mp-field">
                <label>{t('JobApplicants.labelStatus')}</label>
                <select className="mp-input" value={edit.status}
                  onChange={e => setEdit(f => ({ ...f, status: e.target.value }))}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mp-field">
                <label>{t('JobApplicants.labelInterviewAt')}</label>
                <input type="datetime-local" className="mp-input" value={edit.interviewAt}
                  onChange={e => setEdit(f => ({ ...f, interviewAt: e.target.value }))} />
              </div>
              <div className="mp-field">
                <label>{t('JobApplicants.labelInternalNotes')}</label>
                <textarea className="mp-input" rows={3} value={edit.adminNotes}
                  placeholder={t('JobApplicants.notesPlaceholder')}
                  onChange={e => setEdit(f => ({ ...f, adminNotes: e.target.value }))} />
              </div>
              {msg && <div className="mp-msg">{msg}</div>}
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setSelected(null)}>{t('JobApplicants.close')}</button>
              <button className="mp-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('JobApplicants.saving') : <><i className="fas fa-save" /> {t('JobApplicants.save')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
